import { useState, useEffect, useCallback } from "react";
import { integrationApi } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  Zap, Plus, RefreshCw, CheckCircle2, XCircle, AlertCircle, Clock,
  CreditCard, Mail, MessageCircle, ChevronRight, Shield, Activity,
  Settings, Trash2, PlayCircle, RotateCcw, Lock, Unlock, Eye,
  ArrowRight, ShieldCheck, ShieldAlert, Globe
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";

// ── Status helpers ────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  DRAFT:             { color: "bg-slate-100 text-slate-700 border-slate-200",   icon: Clock,          label: "Draft" },
  CONFIGURED:        { color: "bg-blue-100 text-blue-700 border-blue-200",      icon: Settings,       label: "Configured" },
  TESTED:            { color: "bg-cyan-100 text-cyan-700 border-cyan-200",       icon: CheckCircle2,   label: "Tested" },
  APPROVAL_PENDING:  { color: "bg-amber-100 text-amber-700 border-amber-200",   icon: AlertCircle,    label: "Pending Approval" },
  ACTIVE:            { color: "bg-emerald-100 text-emerald-700 border-emerald-200", icon: CheckCircle2, label: "Active" },
  SUSPENDED:         { color: "bg-orange-100 text-orange-700 border-orange-200", icon: Unlock,         label: "Suspended" },
  REVOKED:           { color: "bg-red-100 text-red-700 border-red-200",          icon: XCircle,        label: "Revoked" },
};

const ENV_CONFIG = {
  MOCK:       { color: "bg-slate-100 text-slate-600",  label: "Mock" },
  SANDBOX:    { color: "bg-blue-100 text-blue-700",    label: "Sandbox" },
  PRODUCTION: { color: "bg-emerald-100 text-emerald-700", label: "Production" },
};

const CATEGORY_ICONS = {
  PAYMENT_GATEWAY:    CreditCard,
  EMAIL:              Mail,
  MESSAGING_WHATSAPP: MessageCircle,
  BANKING_API:        Globe,
  CUSTOM:             Zap,
};

function StatusPill({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.DRAFT;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${cfg.color}`}>
      <Icon size={11} />
      {cfg.label}
    </span>
  );
}

function EnvPill({ env }) {
  const cfg = ENV_CONFIG[env] || ENV_CONFIG.MOCK;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cfg.color}`}>
      {cfg.label}
    </span>
  );
}

// ── Provider card in catalog ──────────────────────────────────────────────────

function ProviderCard({ provider, onAdd }) {
  const Icon = CATEGORY_ICONS[provider.category] || Zap;
  return (
    <div
      data-testid={`provider-card-${provider.provider_id}`}
      className="bg-white border border-slate-200 rounded-xl p-5 hover:border-blue-300 hover:shadow-sm transition-all cursor-pointer group"
      onClick={() => onAdd(provider)}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="w-10 h-10 bg-slate-50 rounded-lg flex items-center justify-center border border-slate-200">
          <Icon size={20} className="text-slate-600" />
        </div>
        <span className="text-xs text-slate-400 bg-slate-50 px-2 py-0.5 rounded border">
          {provider.category.replace("_", " ")}
        </span>
      </div>
      <h3 className="text-sm font-semibold text-slate-900 mb-1">{provider.display_name}</h3>
      <p className="text-xs text-slate-500 mb-3 leading-relaxed">{provider.description}</p>
      <div className="flex items-center justify-between">
        <span className="text-xs text-slate-400">
          {provider.required_secret_refs.length} secret ref{provider.required_secret_refs.length !== 1 ? "s" : ""} required
        </span>
        <Button size="sm" variant="outline" className="text-xs h-7 opacity-0 group-hover:opacity-100 transition-opacity">
          <Plus size={12} className="mr-1" />
          Connect
        </Button>
      </div>
    </div>
  );
}

// ── Integration row ───────────────────────────────────────────────────────────

function IntegrationRow({ integration, onSelect }) {
  const Icon = CATEGORY_ICONS[integration.provider_category] || Zap;
  return (
    <div
      data-testid={`integration-row-${integration.id}`}
      className="flex items-center justify-between p-4 bg-white border border-slate-200 rounded-xl hover:border-blue-300 hover:shadow-sm transition-all cursor-pointer"
      onClick={() => onSelect(integration)}
    >
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 bg-slate-50 rounded-lg flex items-center justify-center border border-slate-200">
          <Icon size={16} className="text-slate-600" />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-slate-900">{integration.display_name}</span>
            <StatusPill status={integration.lifecycle_status} />
            <EnvPill env={integration.environment} />
          </div>
          <p className="text-xs text-slate-400 mt-0.5">{integration.instance_id}</p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        {integration.health_check?.status === "HEALTHY" && (
          <span className="flex items-center gap-1 text-xs text-emerald-600">
            <CheckCircle2 size={12} /> Healthy
          </span>
        )}
        {integration.health_check?.status === "UNHEALTHY" && (
          <span className="flex items-center gap-1 text-xs text-red-500">
            <XCircle size={12} /> Unhealthy
          </span>
        )}
        <ChevronRight size={16} className="text-slate-400" />
      </div>
    </div>
  );
}

// ── Add integration modal ─────────────────────────────────────────────────────

function AddIntegrationModal({ provider, onClose, onCreated }) {
  const [form, setForm] = useState({
    display_name: provider.display_name,
    environment: "MOCK",
    secret_refs: {},
    config: {},
    notes: "",
  });
  const [loading, setLoading] = useState(false);

  const handleSecretRef = (key, val) =>
    setForm(f => ({ ...f, secret_refs: { ...f.secret_refs, [key]: val } }));

  const handleConfig = (key, val) =>
    setForm(f => ({ ...f, config: { ...f.config, [key]: val } }));

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const res = await integrationApi.create({
        provider_id: provider.provider_id,
        ...form,
      });
      toast.success(`${provider.display_name} integration created.`);
      onCreated(res.data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create integration.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Connect {provider.display_name}</DialogTitle>
          <DialogDescription>
            Create a new integration instance. Secret references must use{" "}
            <code className="text-xs bg-slate-100 px-1 rounded">env://VAR_NAME</code>,{" "}
            <code className="text-xs bg-slate-100 px-1 rounded">vault://path</code>, or{" "}
            <code className="text-xs bg-slate-100 px-1 rounded">aws-sm://name</code> — never plaintext credentials.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label htmlFor="display_name">Display Name</Label>
            <Input
              id="display_name"
              data-testid="int-display-name"
              value={form.display_name}
              onChange={e => setForm(f => ({ ...f, display_name: e.target.value }))}
              className="mt-1"
            />
          </div>

          <div>
            <Label>Environment</Label>
            <Select value={form.environment} onValueChange={v => setForm(f => ({ ...f, environment: v }))}>
              <SelectTrigger data-testid="int-env-select" className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="MOCK">Mock (no credentials needed)</SelectItem>
                <SelectItem value="SANDBOX">Sandbox (test credentials)</SelectItem>
                <SelectItem value="PRODUCTION">Production (live credentials)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Config fields */}
          {provider.config_fields?.filter(f => !["mode"].includes(f.key)).map(field => (
            <div key={field.key}>
              <Label htmlFor={`config-${field.key}`}>
                {field.key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
                {field.required && <span className="text-red-400 ml-1">*</span>}
              </Label>
              <Input
                id={`config-${field.key}`}
                data-testid={`int-config-${field.key}`}
                placeholder={field.default || ""}
                value={form.config[field.key] || ""}
                onChange={e => handleConfig(field.key, e.target.value)}
                className="mt-1"
              />
            </div>
          ))}

          {/* Secret refs */}
          {provider.required_secret_refs.length > 0 && (
            <div className="border border-amber-200 bg-amber-50 rounded-lg p-3 space-y-3">
              <p className="text-xs font-medium text-amber-700 flex items-center gap-1">
                <Lock size={12} /> Secret References (reference URIs only — not plaintext values)
              </p>
              {provider.required_secret_refs.map(ref => (
                <div key={ref}>
                  <Label className="text-xs">{ref}</Label>
                  <Input
                    data-testid={`int-secret-${ref}`}
                    placeholder={`env://YOUR_${ref.replace(/_ref$/, "").toUpperCase()}`}
                    value={form.secret_refs[ref] || ""}
                    onChange={e => handleSecretRef(ref, e.target.value)}
                    className="mt-1 font-mono text-xs"
                  />
                </div>
              ))}
            </div>
          )}

          <div>
            <Label htmlFor="notes">Notes (optional)</Label>
            <Textarea
              id="notes"
              data-testid="int-notes"
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              className="mt-1 text-sm resize-none"
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>Cancel</Button>
          <Button
            data-testid="int-create-btn"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? "Creating..." : "Create Integration"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Integration detail panel ──────────────────────────────────────────────────

function IntegrationDetail({ integration, catalog, onRefresh, onClose }) {
  const [loading, setLoading] = useState(false);
  const [auditEvents, setAuditEvents] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [approvalNotes, setApprovalNotes] = useState("");
  const [activationNotes, setActivationNotes] = useState("");
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [confirmEnv, setConfirmEnv] = useState("");

  const provider = catalog?.providers?.find(p => p.provider_id === integration.provider_id);
  const Icon = CATEGORY_ICONS[integration.provider_category] || Zap;

  const loadAudit = useCallback(async () => {
    setAuditLoading(true);
    try {
      const res = await integrationApi.getAudit(integration.id, { limit: 20 });
      setAuditEvents(res.data.audit_events || []);
    } catch {
      // silent
    } finally {
      setAuditLoading(false);
    }
  }, [integration.id]);

  useEffect(() => { loadAudit(); }, [loadAudit]);

  const runTest = async () => {
    setLoading(true);
    try {
      const res = await integrationApi.test(integration.id, { test_type: "CONNECTIVITY" });
      const status = res.data.status;
      if (status === "PASS") toast.success("Connection test passed.");
      else if (status === "BLOCKED_BY_CREDENTIALS") toast.warning("Test blocked — credentials not yet configured.");
      else toast.error(`Test failed: ${res.data.detail}`);
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed.");
    } finally {
      setLoading(false);
    }
  };

  const requestActivation = async () => {
    if (integration.environment === "PRODUCTION" && confirmEnv !== "PRODUCTION") {
      toast.error("Type PRODUCTION in the confirmation field to activate production integration.");
      return;
    }
    setLoading(true);
    try {
      await integrationApi.requestActivation(integration.id, {
        notes: activationNotes,
        confirm_environment: integration.environment === "PRODUCTION" ? "PRODUCTION" : integration.environment,
      });
      toast.success("Activation request submitted.");
      setShowActivateModal(false);
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to request activation.");
    } finally {
      setLoading(false);
    }
  };

  const approveActivation = async (decision) => {
    setLoading(true);
    try {
      await integrationApi.approveActivation(integration.id, { decision, notes: approvalNotes });
      toast.success(`Activation ${decision === "APPROVE" ? "approved" : "rejected"}.`);
      setShowApproveModal(false);
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to process approval.");
    } finally {
      setLoading(false);
    }
  };

  const suspend = async () => {
    setLoading(true);
    try {
      await integrationApi.suspend(integration.id);
      toast.success("Integration suspended.");
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to suspend.");
    } finally {
      setLoading(false);
    }
  };

  const s = integration.lifecycle_status;

  return (
    <div data-testid="integration-detail" className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-slate-200">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center">
            <Icon size={20} className="text-slate-600" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-slate-900">{integration.display_name}</h2>
            <p className="text-xs text-slate-400">{integration.instance_id}</p>
          </div>
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
          <XCircle size={20} />
        </button>
      </div>

      {/* Status bar */}
      <div className="flex items-center gap-3 px-6 py-3 bg-slate-50 border-b border-slate-100">
        <StatusPill status={s} />
        <EnvPill env={integration.environment} />
        {integration.sandbox_certified && (
          <span className="flex items-center gap-1 text-xs text-cyan-700 bg-cyan-50 px-2 py-0.5 rounded-full border border-cyan-200">
            <ShieldCheck size={11} /> Sandbox Certified
          </span>
        )}
        {integration.production_certified && (
          <span className="flex items-center gap-1 text-xs text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
            <ShieldCheck size={11} /> Production Certified
          </span>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6 space-y-5">
        {/* Health */}
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
            <Activity size={13} /> Health
          </h3>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-800">
                {integration.health_check?.status || "Unknown"}
              </p>
              {integration.health_check?.last_checked_at && (
                <p className="text-xs text-slate-400">
                  Last checked: {new Date(integration.health_check.last_checked_at).toLocaleString()}
                </p>
              )}
              {integration.health_check?.error_message && (
                <p className="text-xs text-red-500 mt-1">{integration.health_check.error_message}</p>
              )}
            </div>
            <Button size="sm" variant="outline" onClick={runTest} disabled={loading} data-testid="int-test-btn">
              <PlayCircle size={13} className="mr-1" />
              Test Now
            </Button>
          </div>
        </div>

        {/* Secrets (masked) */}
        {integration.secret_refs_masked && Object.keys(integration.secret_refs_masked).length > 0 && (
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
              <Lock size={13} /> Secret References (masked)
            </h3>
            <div className="space-y-2">
              {Object.entries(integration.secret_refs_masked).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between text-xs">
                  <span className="text-slate-500 font-mono">{k}</span>
                  <span className="text-slate-400 font-mono bg-slate-50 px-2 py-0.5 rounded">{v}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Config */}
        {integration.config && Object.keys(integration.config).length > 0 && (
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
              <Settings size={13} /> Configuration
            </h3>
            <div className="space-y-2">
              {Object.entries(integration.config).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between text-xs">
                  <span className="text-slate-500">{k.replace(/_/g, " ")}</span>
                  <span className="text-slate-700 font-mono">{String(v)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
            <Zap size={13} /> Actions
          </h3>
          <div className="flex flex-wrap gap-2">
            {s === "TESTED" && (
              <Button
                size="sm"
                variant="outline"
                className="text-xs"
                onClick={() => setShowActivateModal(true)}
                data-testid="int-request-activation-btn"
              >
                <ArrowRight size={12} className="mr-1" />
                Request Activation
              </Button>
            )}
            {s === "APPROVAL_PENDING" && (
              <Button
                size="sm"
                className="text-xs bg-emerald-600 hover:bg-emerald-700"
                onClick={() => setShowApproveModal(true)}
                data-testid="int-approve-btn"
              >
                <CheckCircle2 size={12} className="mr-1" />
                Review Approval
              </Button>
            )}
            {s === "ACTIVE" && (
              <Button
                size="sm"
                variant="outline"
                className="text-xs text-orange-600 border-orange-200"
                onClick={suspend}
                disabled={loading}
                data-testid="int-suspend-btn"
              >
                <Unlock size={12} className="mr-1" />
                Suspend
              </Button>
            )}
          </div>
        </div>

        {/* Audit trail */}
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
            <Shield size={13} /> Audit Trail
          </h3>
          {auditLoading ? (
            <p className="text-xs text-slate-400">Loading...</p>
          ) : auditEvents.length === 0 ? (
            <p className="text-xs text-slate-400">No audit events yet.</p>
          ) : (
            <div className="space-y-2">
              {auditEvents.map(ev => (
                <div key={ev.id} className="flex items-start gap-2 text-xs">
                  <span className="text-slate-300 mt-0.5">•</span>
                  <div>
                    <span className="font-medium text-slate-700">{ev.action}</span>
                    <span className="text-slate-400 ml-2">{new Date(ev.timestamp).toLocaleString()}</span>
                    <p className="text-slate-500 mt-0.5">{ev.summary}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Activate modal */}
      {showActivateModal && (
        <Dialog open onOpenChange={() => setShowActivateModal(false)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Request Activation</DialogTitle>
              <DialogDescription>
                Submit this integration for approval. Production activation requires a separate approver (SoD).
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              {integration.environment === "PRODUCTION" && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm font-semibold text-red-700 flex items-center gap-1.5 mb-2">
                    <ShieldAlert size={14} /> Production Confirmation Required
                  </p>
                  <p className="text-xs text-red-600 mb-2">
                    Type <strong>PRODUCTION</strong> below to confirm you are requesting live production activation.
                  </p>
                  <Input
                    data-testid="int-confirm-env"
                    value={confirmEnv}
                    onChange={e => setConfirmEnv(e.target.value)}
                    placeholder="Type PRODUCTION to confirm"
                    className="font-mono text-sm"
                  />
                </div>
              )}
              <div>
                <Label>Notes (optional)</Label>
                <Textarea
                  data-testid="int-activation-notes"
                  value={activationNotes}
                  onChange={e => setActivationNotes(e.target.value)}
                  rows={2}
                  className="mt-1 text-sm resize-none"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowActivateModal(false)}>Cancel</Button>
              <Button
                data-testid="int-submit-activation-btn"
                onClick={requestActivation}
                disabled={loading || (integration.environment === "PRODUCTION" && confirmEnv !== "PRODUCTION")}
              >
                Submit for Approval
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Approve modal */}
      {showApproveModal && (
        <Dialog open onOpenChange={() => setShowApproveModal(false)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Review Activation Request</DialogTitle>
              <DialogDescription>
                You must be a different person than who requested activation (SoD rule).
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700">
                <p className="font-medium mb-1">Requested by: {integration.approval?.requested_by_email}</p>
                <p className="text-amber-600">Notes: {integration.approval?.activation_notes || "None"}</p>
              </div>
              <div>
                <Label>Your Notes (optional)</Label>
                <Textarea
                  data-testid="int-approval-notes"
                  value={approvalNotes}
                  onChange={e => setApprovalNotes(e.target.value)}
                  rows={2}
                  className="mt-1 text-sm resize-none"
                />
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                className="text-red-600 border-red-200"
                data-testid="int-reject-btn"
                onClick={() => approveActivation("REJECT")}
                disabled={loading}
              >
                <XCircle size={13} className="mr-1" /> Reject
              </Button>
              <Button
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="int-approve-confirm-btn"
                onClick={() => approveActivation("APPROVE")}
                disabled={loading}
              >
                <CheckCircle2 size={13} className="mr-1" /> Approve Activation
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// ── Main Integration Control Center page ──────────────────────────────────────

export default function IntegrationControlCenter() {
  const { user } = useAuth();
  const [tab, setTab] = useState("connected");
  const [catalog, setCatalog] = useState(null);
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addingProvider, setAddingProvider] = useState(null);
  const [selectedIntegration, setSelectedIntegration] = useState(null);

  const canConfigure = user?.capabilities?.includes("integration.configure");
  const canView = user?.capabilities?.includes("integration.view");

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [catRes, intRes] = await Promise.all([
        integrationApi.getCatalog(),
        integrationApi.list(),
      ]);
      setCatalog(catRes.data);
      setIntegrations(intRes.data?.integrations || []);
    } catch (e) {
      if (e?.response?.status !== 403) {
        toast.error("Failed to load integrations.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const refreshSelected = async () => {
    await loadAll();
    if (selectedIntegration) {
      try {
        const res = await integrationApi.get(selectedIntegration.id);
        setSelectedIntegration(res.data);
      } catch {
        setSelectedIntegration(null);
      }
    }
  };

  const TABS = [
    { id: "connected", label: "Connected", count: integrations.length },
    { id: "catalog", label: "Provider Catalog", count: catalog?.count },
  ];

  return (
    <div data-testid="integration-control-center" className="flex h-full">
      {/* Main panel */}
      <div className={`flex-1 flex flex-col min-w-0 ${selectedIntegration ? "hidden lg:flex" : "flex"}`}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4">
          <div>
            <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
              <Zap size={20} className="text-blue-600" />
              Integration Control Center
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Manage external provider connections, credentials and activation governance.
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={loadAll}
            disabled={loading}
            data-testid="int-refresh-btn"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-6 border-b border-slate-200 mb-0">
          {TABS.map(t => (
            <button
              key={t.id}
              data-testid={`int-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                tab === t.id
                  ? "border-blue-600 text-blue-700"
                  : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
            >
              {t.label}
              {t.count != null && (
                <span className="text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-full">
                  {t.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <RefreshCw size={20} className="animate-spin text-slate-400" />
            </div>
          ) : tab === "connected" ? (
            <div className="space-y-3">
              {integrations.length === 0 ? (
                <div className="text-center py-16 text-slate-400">
                  <Zap size={32} className="mx-auto mb-3 opacity-30" />
                  <p className="text-sm font-medium">No integrations connected yet.</p>
                  <p className="text-xs mt-1">Switch to Provider Catalog to add one.</p>
                  <Button
                    size="sm"
                    variant="outline"
                    className="mt-4"
                    onClick={() => setTab("catalog")}
                    data-testid="int-go-catalog-btn"
                  >
                    Browse Providers
                  </Button>
                </div>
              ) : (
                integrations.map(i => (
                  <IntegrationRow
                    key={i.id}
                    integration={i}
                    onSelect={setSelectedIntegration}
                  />
                ))
              )}
            </div>
          ) : (
            <div>
              <p className="text-sm text-slate-500 mb-4">
                Select a provider to create a new integration. Configuration is schema-driven.
                {!canConfigure && (
                  <span className="ml-2 text-amber-600">
                    (Read-only — you do not have the integration.configure capability.)
                  </span>
                )}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {(catalog?.providers || []).map(p => (
                  <ProviderCard
                    key={p.provider_id}
                    provider={p}
                    onAdd={canConfigure ? setAddingProvider : () => {}}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Detail panel (right side) */}
      {selectedIntegration && (
        <div className="w-full lg:w-[420px] border-l border-slate-200 bg-white overflow-hidden flex-shrink-0">
          <IntegrationDetail
            integration={selectedIntegration}
            catalog={catalog}
            onRefresh={refreshSelected}
            onClose={() => setSelectedIntegration(null)}
          />
        </div>
      )}

      {/* Add integration modal */}
      {addingProvider && (
        <AddIntegrationModal
          provider={addingProvider}
          onClose={() => setAddingProvider(null)}
          onCreated={(newInt) => {
            setIntegrations(prev => [newInt, ...prev]);
            setSelectedIntegration(newInt);
            setTab("connected");
          }}
        />
      )}
    </div>
  );
}
